import type { IGatewayResponse, IInputDatasetSummaryResponse, TDatasetSort } from '@escloud/types';
import { ChevronDown, Loader2, Search, Upload } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SlotCard, PatientLockBanner, type SlotDescriptor } from './slot-card';
import { useEagerDicomUpload, type EagerUploadFailureReason } from './use-eager-dicom-upload';

import { DatasetMultiPaneViewer } from '@/components/cases/dataset-multi-pane-viewer';
import { DicomVolumeImporter } from '@/components/cases/dicom-volume-importer';
import { SeriesSummaryCard } from '@/components/cases/series-summary-card';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/context';
import { useDataset, useDatasetSearch } from '@/hooks/use-datasets';
import { useGateways } from '@/hooks/use-gateways';
import type { DicomImportResult, DicomMetadata } from '@/lib/dicom';
import { formatDicomDate, formatDicomName } from '@/lib/dicom';
import { cn } from '@/lib/utils';
import type { SearchDatasetsParams } from '@/services';

interface ImagingStepProps {
    /** Workflow id from the URL — needed to address the upload session. */
    workflowId: string;
    /** Every VOLUME slot in the workflow, in declaration order. */
    slots: SlotDescriptor[];
    /**
     * Slot key → bound InputDataset id. Both catalogue picks and
     * eager-uploaded reconstructions land here; the imaging step
     * never holds raw `DicomImportResult` buffers between renders.
     */
    slotPicks: Record<string, string>;
    /**
     * Patient that subsequent picks are constrained to, or null when
     * no lock is engaged. `id` drives the server-side filter; `name`
     * renders the lock banner without depending on the current result
     * list. Released when the user clicks "Change patient".
     */
    lockedPatient: { id: string; name: string | null } | null;
    /**
     * Update or clear a single slot pick. The patient `id` + `name`
     * are passed when a row is picked so the wizard can engage the
     * patient lock with a stable display name.
     */
    onPickChange: (
        slotKey: string,
        datasetId: string | null,
        patientId?: string | null,
        patientName?: string | null
    ) => void;
    /** Optional DICOM metadata pre-fill hook (forwarded to PatientDetailsStep). */
    onDicomMetadata?: (metadata: DicomMetadata) => void;
    /**
     * Confirmed "Change patient" — releases the lock AND resets all
     * picks. The confirmation prompt lives inside this step.
     */
    onChangePatient: () => void;
}

type FilterFields = Omit<SearchDatasetsParams, 'cursor' | 'limit'>;

const DEFAULT_SORT: TDatasetSort = 'createdAt_desc';

function initialFilters(): FilterFields {
    return {
        sort: DEFAULT_SORT,
    };
}

/**
 * Imaging step — single canvas, multi-slot aware.
 *
 * Layout:
 *   ┌─[ Slot strip (>1 slot only) ]──────────────────────────────┐
 *   ├─[ Patient lock banner (when locked) ]──────────────────────┤
 *   ├─[ Filter rail │ Result list │ Preview ]───────────────────┤
 *   │  ▶ rail: active-slot context, search, filters, upload      │
 *   │  ▶ list: client-grouped patient cards, scored per slot     │
 *   │  ▶ preview: DatasetPreviewViewer of the active selection   │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Suitability scoring (Suitable/Marginal/Unsuitable badges, "Best
 * for <slot>" hints, "In use · <other-slot>" warnings) is
 * intentionally absent — Phase 3 ingests the data, the rules engine
 * lands in a future phase.
 */
export function ImagingStep({
    workflowId,
    slots,
    slotPicks,
    lockedPatient,
    onPickChange,
    onDicomMetadata,
    onChangePatient,
}: ImagingStepProps) {
    // Active slot focus: first empty required slot, falling back to
    // the first slot. Both picks and eager uploads land in slotPicks.
    const initialActive = useMemo(() => {
        const firstEmptyRequired = slots.find((s) => s.definition.required !== false && !slotPicks[s.key]);
        return firstEmptyRequired?.key ?? slots[0]?.key ?? null;
    }, [slots, slotPicks]);
    const [activeSlotKey, setActiveSlotKey] = useState<string | null>(initialActive);
    const [uploadSheetOpen, setUploadSheetOpen] = useState(false);
    const activeSlot = useMemo(
        () => slots.find((s) => s.key === activeSlotKey) ?? slots[0] ?? null,
        [slots, activeSlotKey]
    );

    const { tenantId } = useTenant();
    const { data: gatewaysPage } = useGateways(tenantId ?? undefined);
    const gateways = gatewaysPage?.items ?? [];
    const [filters, setFilters] = useState<FilterFields>(initialFilters);
    const [searchText, setSearchText] = useState('');
    const [confirmChangePatientOpen, setConfirmChangePatientOpen] = useState(false);
    // Local selection drives the preview pane. A row is "selected"
    // until the user clicks "Use for this slot", at which point the
    // wizard's slotPicks takes over. We keep the active slot's pick
    // pre-selected so re-entering the step shows the preview again.
    const [pendingSelectionId, setPendingSelectionId] = useState<string | null>(null);

    // Server-side filters: patient lock translates to a substring
    // patientId filter, which is exact-equivalent on a known id.
    const queryParams = useMemo<FilterFields>(() => {
        const base = stripEmpty(filters);
        if (lockedPatient) {
            return { ...base, patientId: lockedPatient.id };
        }
        return base;
    }, [filters, lockedPatient]);

    const debouncedSearchText = useDebouncedValue(searchText, 300);

    const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useDatasetSearch(queryParams);

    const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
    const totalCount = data?.pages[0]?.totalCount ?? 0;

    // Client-side fuzzy over loaded rows; server-side `q` deferred.
    const visibleItems = useMemo(
        () => filterByText(items, debouncedSearchText),
        [items, debouncedSearchText]
    );

    const groupedItems = useMemo(() => groupByStudy(visibleItems), [visibleItems]);

    // Used-in-other-slot warnings: same dataset can't fill two slots.
    const otherSlotByDatasetId = useMemo(() => {
        const map = new Map<string, string>();
        for (const [slotKey, id] of Object.entries(slotPicks)) {
            if (slotKey === activeSlotKey) continue;
            const slotName = slots.find((s) => s.key === slotKey)?.definition.name ?? slotKey;
            map.set(id, slotName);
        }
        return map;
    }, [slotPicks, slots, activeSlotKey]);

    const sentinelRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting) && hasNextPage && !isFetchingNextPage) {
                    void fetchNextPage();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // SlotCard pick label covers both "picked from catalogue" and
    // "reconstructed from DICOM folder". Picked rows show their
    // series description; reconstructions show a "Reconstructed"
    // marker so the strip reads consistently across the two paths.
    const pickLabels = useMemo(() => {
        const map: Record<string, string> = {};
        for (const [slotKey, datasetId] of Object.entries(slotPicks)) {
            const row = items.find((i) => i.id === datasetId);
            map[slotKey] = row?.seriesDescription ?? `Dataset …${datasetId.slice(-8)}`;
        }
        return map;
    }, [slotPicks, items]);

    const handleSelect = useCallback((id: string | null) => {
        setPendingSelectionId(id);
    }, []);

    const handleUseForSlot = useCallback(() => {
        if (!activeSlot || !pendingSelectionId) return;
        const row = items.find((i) => i.id === pendingSelectionId);
        onPickChange(activeSlot.key, pendingSelectionId, row?.patientId ?? null, row?.patientName ?? null);

        // Auto-advance to the next empty required slot for a smooth
        // multi-input flow. Falls back to the next empty slot of any
        // kind, then stays put.
        const nextRequired = slots.find(
            (s) => s.key !== activeSlot.key && s.definition.required !== false && !slotPicks[s.key]
        );
        const nextAny = nextRequired ?? slots.find((s) => s.key !== activeSlot.key && !slotPicks[s.key]);
        if (nextAny) {
            setActiveSlotKey(nextAny.key);
            setPendingSelectionId(null);
        }
    }, [activeSlot, pendingSelectionId, items, onPickChange, slots, slotPicks]);

    const handleClearSlot = useCallback(
        (slotKey: string) => {
            onPickChange(slotKey, null);
            setActiveSlotKey(slotKey);
            setPendingSelectionId(null);
        },
        [onPickChange]
    );

    const eagerUpload = useEagerDicomUpload();

    /**
     * Reconstruction completed in the worker — push the bytes to the
     * server immediately. On success we treat the new `InputDataset`
     * as a normal pick (slotPick + patient lock); on failure we leave
     * the error in the sheet so the user can retry.
     *
     * No per-slot reconstruction state is kept in wizard memory; the
     * server is the single source of truth for "what fills this slot".
     */
    const handleDicomResult = useCallback(
        async (result: DicomImportResult | null) => {
            if (!activeSlot) return;
            if (result === null) {
                eagerUpload.reset();
                return;
            }

            const outcome = await eagerUpload.upload({
                workflowId,
                slotKey: activeSlot.key,
                result,
            });
            if (!outcome) return;

            onPickChange(activeSlot.key, outcome.inputDatasetId, outcome.patientId, outcome.patientName);
            setUploadSheetOpen(false);
            eagerUpload.reset();

            const nextRequired = slots.find(
                (s) => s.key !== activeSlot.key && s.definition.required !== false && !slotPicks[s.key]
            );
            if (nextRequired) {
                setActiveSlotKey(nextRequired.key);
                setPendingSelectionId(null);
            }
        },
        [activeSlot, eagerUpload, onPickChange, slots, slotPicks, workflowId]
    );

    const updateFilter = (key: keyof FilterFields, value: string | undefined) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    /**
     * Saved-search chips mutate multiple filter keys at once (e.g.
     * selecting a gateway also pins `source='gateway'`). The chip
     * callback patches the filter state in one go so the query only
     * fires once per click.
     */
    const applyChipPatch = (patch: Partial<FilterFields>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    };

    const resetFilters = () => {
        setFilters(initialFilters());
        setSearchText('');
    };

    if (!activeSlot) {
        return null;
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
            {slots.length > 1 && (
                <div className="flex gap-3">
                    {slots.map((slot, idx) => (
                        <SlotCard
                            key={slot.key}
                            slot={slot}
                            index={idx}
                            active={activeSlotKey === slot.key}
                            filled={!!slotPicks[slot.key]}
                            pickLabel={pickLabels[slot.key] ?? null}
                            onClick={() => {
                                setActiveSlotKey(slot.key);
                                setPendingSelectionId(slotPicks[slot.key] ?? null);
                            }}
                            onClear={() => handleClearSlot(slot.key)}
                        />
                    ))}
                </div>
            )}

            {lockedPatient && (
                <PatientLockBanner
                    patientLabel={formatDicomName(lockedPatient.name) ?? lockedPatient.id}
                    onChangePatient={() => setConfirmChangePatientOpen(true)}
                />
            )}

            {/* No min-height floor: the app shell bounds the page, so
                the three panes compress and scroll internally instead
                of pushing the footer/CTA out of view (ESCLOUD-247).
                Below md the panes stack; the grid then scrolls as a
                whole since per-pane scrolling makes no sense there. */}
            {/* md:grid-rows-[minmax(0,1fr)] pins the single row to the
                container height — an auto row would grow to the
                tallest pane's content and defeat per-pane scrolling. */}
            <div className="grid min-h-0 flex-1 grid-cols-1 max-md:overflow-y-auto md:grid-cols-[15rem_minmax(0,1fr)_24rem] md:grid-rows-[minmax(0,1fr)] border-t">
                <FilterRail
                    activeSlot={activeSlot}
                    filters={filters}
                    gateways={gateways}
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    onChange={updateFilter}
                    onChipSelect={applyChipPatch}
                    onReset={resetFilters}
                    onUploadClick={() => setUploadSheetOpen(true)}
                />

                <section className="flex min-h-0 flex-col rounded-md border-l border-r bg-surface-warm">
                    <header className="flex items-center gap-2 border-b px-3 py-2">
                        <p className="text-[11px] text-muted-foreground">
                            {isLoading
                                ? 'Searching…'
                                : `${totalCount} dataset${totalCount === 1 ? '' : 's'} found`}
                        </p>
                        <span className="text-[11px] text-muted-foreground ml-auto">Sort by</span>
                        <Select
                            value={filters.sort ?? DEFAULT_SORT}
                            onValueChange={(v) => updateFilter('sort', v as TDatasetSort)}
                        >
                            <SelectTrigger className="h-7 w-auto gap-1 border-0 bg-transparent px-1 text-[11px] shadow-none hover:bg-muted/50 focus-visible:ring-0">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="studyDate_desc">Newest study date</SelectItem>
                                <SelectItem value="studyDate_asc">Oldest study date</SelectItem>
                                <SelectItem value="createdAt_desc">Recently uploaded</SelectItem>
                                <SelectItem value="createdAt_asc">First uploaded</SelectItem>
                            </SelectContent>
                        </Select>
                    </header>
                    <ResultList
                        groups={groupedItems}
                        selectedId={pendingSelectionId}
                        onSelect={handleSelect}
                        usedInOtherSlot={otherSlotByDatasetId}
                        isLoading={isLoading && items.length === 0}
                        isFetchingNextPage={isFetchingNextPage}
                        sentinelRef={sentinelRef}
                        emptyMessage={
                            lockedPatient
                                ? 'No matching series for the locked patient.'
                                : 'No datasets match your filters.'
                        }
                    />
                </section>

                <PreviewPane
                    activeSlotName={activeSlot.definition.name}
                    pendingSelectionId={pendingSelectionId}
                    alreadyPickedForActiveSlot={
                        !!activeSlot && slotPicks[activeSlot.key] === pendingSelectionId
                    }
                    usedInOtherSlot={
                        pendingSelectionId ? (otherSlotByDatasetId.get(pendingSelectionId) ?? null) : null
                    }
                    onUseForSlot={handleUseForSlot}
                />
            </div>

            <Sheet
                open={uploadSheetOpen}
                onOpenChange={(open) => {
                    // Sheet close mid-upload aborts the in-flight
                    // request rather than silently swallowing the
                    // click, then resets state.
                    if (!open && eagerUpload.status.kind === 'uploading') {
                        eagerUpload.abort();
                    }
                    setUploadSheetOpen(open);
                    if (!open) eagerUpload.reset();
                }}
            >
                <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
                    <SheetHeader>
                        <SheetTitle>Upload DICOM for {activeSlot.definition.name}</SheetTitle>
                        <SheetDescription>
                            Pick a DICOM folder to extract a series. The reconstructed volume is uploaded
                            immediately and bound to the case at creation.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="px-4">
                        {eagerUpload.status.kind === 'uploading' ? (
                            <div className="flex flex-col items-center gap-3 py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                <p className="text-sm font-medium">
                                    {uploadingPhaseLabel(eagerUpload.status.phase)}
                                </p>
                                <div className="w-full max-w-xs">
                                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                                        <div
                                            className="h-full bg-primary transition-[width] duration-150"
                                            style={{
                                                width: `${eagerUpload.status.progress}%`,
                                            }}
                                        />
                                    </div>
                                    <p className="mt-1 text-center text-xs text-muted-foreground">
                                        {eagerUpload.status.progress}%
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => eagerUpload.abort()}
                                >
                                    Cancel upload
                                </Button>
                            </div>
                        ) : eagerUpload.status.kind === 'error' ? (
                            <div className="flex flex-col items-start gap-3 py-6 text-sm">
                                <p className="text-destructive">
                                    {errorReasonLabel(eagerUpload.status.reason)}
                                </p>
                                <p className="text-xs text-muted-foreground">{eagerUpload.status.message}</p>
                                <Button type="button" variant="outline" onClick={() => eagerUpload.reset()}>
                                    Try again
                                </Button>
                            </div>
                        ) : eagerUpload.status.kind === 'aborted' ? (
                            <div className="flex flex-col items-start gap-3 py-6 text-sm">
                                <p className="text-muted-foreground">Upload cancelled.</p>
                                <Button type="button" variant="outline" onClick={() => eagerUpload.reset()}>
                                    Try again
                                </Button>
                            </div>
                        ) : (
                            <DicomVolumeImporter
                                slot={{
                                    key: activeSlot.key,
                                    name: activeSlot.definition.name,
                                    type: activeSlot.definition.type,
                                    required: activeSlot.definition.required,
                                    description: activeSlot.definition.description,
                                }}
                                onChange={handleDicomResult}
                                onMetadata={onDicomMetadata}
                            />
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={confirmChangePatientOpen} onOpenChange={setConfirmChangePatientOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Change patient?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Switching patients clears every slot you have filled so far. Multi-input workflows
                            assume one patient per case, so picks across patients aren't allowed. Continue?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                onChangePatient();
                                setPendingSelectionId(null);
                                setActiveSlotKey(slots[0]?.key ?? null);
                            }}
                        >
                            Change patient
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function FilterRail({
    activeSlot,
    filters,
    gateways,
    searchText,
    onSearchTextChange,
    onChange,
    onChipSelect,
    onReset,
    onUploadClick,
}: {
    activeSlot: SlotDescriptor;
    filters: FilterFields;
    gateways: IGatewayResponse[];
    searchText: string;
    onSearchTextChange: (value: string) => void;
    onChange: (key: keyof FilterFields, value: string | undefined) => void;
    onChipSelect: (patch: Partial<FilterFields>) => void;
    onReset: () => void;
    onUploadClick: () => void;
}) {
    return (
        <aside className="flex flex-col gap-3 rounded-md py-3 pr-3 text-xs">
            <RailSearchInput value={searchText} onChange={onSearchTextChange} />

            <FilterSection label="Quick Filters">
                <SavedSearchChips filters={filters} gateways={gateways} onSelect={onChipSelect} />
            </FilterSection>

            <FilterSection label="Study date">
                <FilterDateInput
                    label="From"
                    value={filters.studyDateFrom ?? ''}
                    onChange={(v) => onChange('studyDateFrom', v || undefined)}
                />
                <FilterDateInput
                    label="To"
                    value={filters.studyDateTo ?? ''}
                    onChange={(v) => onChange('studyDateTo', v || undefined)}
                />
            </FilterSection>

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={onReset}
                    className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                    Reset
                </button>
            </div>

            <div className="flex-1 h-full border-b"></div>

            {/* Upload-for-slot anchored at the bottom. Opens a Sheet
                with the DicomVolumeImporter wired to the active slot. */}
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed text-xs"
                onClick={onUploadClick}
            >
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Upload for {activeSlot.definition.name}
            </Button>
        </aside>
    );
}

/**
 * Saved-search chip row. Rendered above the filter inputs as a single
 * horizontal stack so the wizard's three most common scopes ("all",
 * "only direct uploads", "only from gateway X") plus a recency preset
 * ("uploaded today") are one click away.
 *
 * Chip semantics:
 *   - "All" — clears `source`, `gatewayId` (recency stays untouched).
 *   - "Direct uploads" — `source='direct_upload'`, clears `gatewayId`.
 *   - "From <gw>" — `source='gateway'`, `gatewayId=<gw.id>`.
 *   - "Pushed today" — toggles `createdAfter` between midnight UTC and
 *     undefined. Orthogonal to the source chips so users can combine
 *     "From <gw>" + "Pushed today" to triage the day's inbox.
 */
function SavedSearchChips({
    filters,
    gateways,
    onSelect,
}: {
    filters: FilterFields;
    gateways: IGatewayResponse[];
    onSelect: (patch: Partial<FilterFields>) => void;
}) {
    const todayStart = startOfTodayUtcIso();
    const scopeAll = !filters.source && !filters.gatewayId;
    const scopeDirect = filters.source === 'direct_upload';
    const todayActive = filters.createdAfter === todayStart;

    return (
        <div className="flex flex-wrap gap-1">
            <Chip active={scopeAll} onClick={() => onSelect({ source: undefined, gatewayId: undefined })}>
                All
            </Chip>
            <Chip
                active={scopeDirect}
                onClick={() => onSelect({ source: 'direct_upload', gatewayId: undefined })}
            >
                Direct uploads
            </Chip>
            {gateways.map((gw) => {
                const active = filters.gatewayId === gw.id;
                return (
                    <Chip
                        key={gw.id}
                        active={active}
                        onClick={() => onSelect({ source: 'gateway', gatewayId: gw.id })}
                    >
                        From {gw.name}
                    </Chip>
                );
            })}
            <Chip
                active={todayActive}
                onClick={() => onSelect({ createdAfter: todayActive ? undefined : todayStart })}
            >
                Pushed today
            </Chip>
        </div>
    );
}

/** Midnight UTC of the current day as an ISO 8601 string. */
function startOfTodayUtcIso(): string {
    const d = new Date();
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();
}

function Chip({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'rounded border px-2 py-0.5 text-[11px] font-medium transition-colors',
                active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-muted-foreground hover:text-foreground'
            )}
        >
            {children}
        </button>
    );
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="space-y-1.5">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
            {children}
        </div>
    );
}

/**
 * Single search input at the top of the filter rail. Replaces the
 * earlier triplet of "Patient name / MRN / Accession" fields plus the
 * separate top-of-pane search bar — the client-side fuzzy match
 * already spans all of those fields, so one box covers all the cases
 * a user would reach for the per-field inputs.
 */
function RailSearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    return (
        <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Search …"
                className="h-8 pl-7 text-xs"
            />
        </div>
    );
}

function FilterDateInput({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
}) {
    const iso = value.length === 8 ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : value;
    return (
        <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</Label>
            <Input
                type="date"
                value={iso}
                onChange={(e) => onChange(e.target.value ? e.target.value.replace(/-/g, '') : '')}
                className="h-8 text-sm"
            />
        </div>
    );
}

interface StudyGroup {
    patientId: string | null;
    patientName: string | null;
    studyDate: string | null;
    studyDescription: string | null;
    items: IInputDatasetSummaryResponse[];
}

function ResultList({
    groups,
    selectedId,
    onSelect,
    usedInOtherSlot,
    isLoading,
    isFetchingNextPage,
    sentinelRef,
    emptyMessage,
}: {
    groups: StudyGroup[];
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    usedInOtherSlot: Map<string, string>;
    isLoading: boolean;
    isFetchingNextPage: boolean;
    sentinelRef: React.RefObject<HTMLDivElement | null>;
    emptyMessage: string;
}) {
    if (isLoading) {
        return (
            <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                ))}
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="flex flex-1 flex-col items-center justify-center p-8 text-center text-sm text-muted-foreground">
                <Search className="mb-2 h-8 w-8" />
                {emptyMessage}
            </div>
        );
    }

    return (
        <ResultListBody
            {...{ groups, selectedId, onSelect, usedInOtherSlot, isFetchingNextPage, sentinelRef }}
        />
    );
}

/**
 * Inner body kept separate so the hook for tracking the currently
 * expanded patient group sits below the early-return guards above
 * (loading / empty states), keeping the hook-call order stable.
 */
function ResultListBody({
    groups,
    selectedId,
    onSelect,
    usedInOtherSlot,
    isFetchingNextPage,
    sentinelRef,
}: {
    groups: StudyGroup[];
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    usedInOtherSlot: Map<string, string>;
    isFetchingNextPage: boolean;
    sentinelRef: React.RefObject<HTMLDivElement | null>;
}) {
    // Exclusive-open accordion: only one patient group is expanded
    // at a time. Default to the group containing the current
    // pending selection (so re-entering the step lands users on
    // their selection) and fall back to the first group otherwise.
    const groupKey = (g: StudyGroup) => `${g.patientId ?? 'np'}-${g.studyDate ?? 'nd'}`;
    const defaultExpanded = useMemo(() => {
        if (selectedId) {
            const found = groups.find((g) => g.items.some((it) => it.id === selectedId));
            if (found) return groupKey(found);
        }
        return groups[0] ? groupKey(groups[0]) : null;
    }, [groups, selectedId]);
    const [expandedKey, setExpandedKey] = useState<string | null>(defaultExpanded);
    useEffect(() => {
        setExpandedKey(defaultExpanded);
    }, [defaultExpanded]);

    return (
        <div className="flex-1 overflow-y-auto p-2">
            {groups.map((group) => {
                const k = groupKey(group);
                return (
                    <PatientGroup
                        key={k}
                        group={group}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        usedInOtherSlot={usedInOtherSlot}
                        expanded={expandedKey === k}
                        onToggle={() => setExpandedKey((prev) => (prev === k ? null : k))}
                    />
                );
            })}
            <div ref={sentinelRef} className="h-4" />
            {isFetchingNextPage && (
                <div className="py-2 text-center text-xs text-muted-foreground">Loading more…</div>
            )}
        </div>
    );
}

function PatientGroup({
    group,
    selectedId,
    onSelect,
    usedInOtherSlot,
    expanded,
    onToggle,
}: {
    group: StudyGroup;
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    usedInOtherSlot: Map<string, string>;
    expanded: boolean;
    onToggle: () => void;
}) {
    const seriesCount = group.items.length;
    return (
        <div className={cn('mb-3 overflow-hidden rounded-xl border bg-card', expanded && 'drop-shadow-xs')}>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className={cn(
                    'flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:cursor-pointer',
                    expanded && 'border-b'
                )}
            >
                <ChevronDown
                    className={cn(
                        'h-4 w-4 text-muted-foreground transition-transform',
                        !expanded && '-rotate-90'
                    )}
                    aria-hidden
                />
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">
                        {formatDicomName(group.patientName) ?? group.patientId ?? 'Unknown patient'}
                    </p>
                    {(group.studyDate || group.studyDescription) && (
                        <p className="truncate text-[11px] text-muted-foreground">
                            {[formatDicomDate(group.studyDate), group.studyDescription]
                                .filter(Boolean)
                                .join(' — ')}
                        </p>
                    )}
                </div>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {seriesCount} series
                </span>
            </button>
            {expanded && (
                <ul className="divide-y bg-surface-warm-elevated">
                    {group.items.map((item) => (
                        <li key={item.id}>
                            <SeriesRow
                                item={item}
                                selected={selectedId === item.id}
                                usedInOtherSlot={usedInOtherSlot.get(item.id) ?? null}
                                onClick={() => onSelect(item.id)}
                            />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function SeriesRow({
    item,
    selected,
    usedInOtherSlot,
    onClick,
}: {
    item: IInputDatasetSummaryResponse;
    selected: boolean;
    usedInOtherSlot: string | null;
    onClick: () => void;
}) {
    const inlineSummary = formatSeriesInline(item);
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                // Indent series rows under their patient header (the
                // chevron in the header sits at ~12px from the card
                // edge; matching that here makes the rows read as
                // children of the group, not standalone blocks).
                'group relative w-full border-l-2 border-transparent py-2 pl-6 pr-3 text-left transition',
                'hover:cursor-pointer',
                selected && 'border-l-primary bg-primary/10'
            )}
        >
            <div className="flex items-start justify-between gap-2 ">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <p
                            className={cn(
                                'truncate text-sm',
                                selected ? 'font-semibold text-foreground' : 'font-medium'
                            )}
                        >
                            {item.seriesDescription ?? 'Series'}
                        </p>
                        {usedInOtherSlot && (
                            <Badge
                                variant="outline"
                                className="shrink-0 border-amber-600/40 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                            >
                                In use · {usedInOtherSlot}
                            </Badge>
                        )}
                    </div>
                    {inlineSummary && (
                        <p className="truncate text-[11px] text-muted-foreground">{inlineSummary}</p>
                    )}
                </div>
                {item.modality && (
                    <Badge variant="secondary" className="shrink-0">
                        {item.modality}
                    </Badge>
                )}
            </div>
        </button>
    );
}

function PreviewPane({
    activeSlotName,
    pendingSelectionId,
    alreadyPickedForActiveSlot,
    usedInOtherSlot,
    onUseForSlot,
}: {
    activeSlotName: string;
    pendingSelectionId: string | null;
    alreadyPickedForActiveSlot: boolean;
    usedInOtherSlot: string | null;
    onUseForSlot: () => void;
}) {
    // Cache-shared with the picker's result list; no extra
    // roundtrip when the user picks a row already in the loaded
    // window.
    const { data: datasetDetail, isLoading: detailLoading } = useDataset(pendingSelectionId ?? undefined);

    return (
        <section className="flex min-h-0 flex-col rounded-md">
            {/* Viewer height never exceeds its width: aspect-square
                sets the flex basis to the pane width and the default
                flex shrink lets it compress (down to the floor) when
                the pane is short — so it renders square at best,
                landscape under pressure, never portrait. The
                ResizeObserver in DatasetMultiPaneViewer re-fits the
                camera on every size change. On stacked (<md) layouts
                a fixed height replaces the square rule — the pane is
                page-wide there and a square would be enormous. */}
            <div className="aspect-square w-full min-h-40 max-md:h-80">
                <DatasetMultiPaneViewer dataset={datasetDetail ?? null} />
            </div>

            {/* Metadata takes its natural height; when the pane runs
                short it shrinks and scrolls internally — viewer floor
                and CTA always stay put. */}
            <div className="min-h-0 overflow-y-auto p-3">
                {datasetDetail && (
                    <SeriesSummaryCard
                        dataset={datasetDetail ?? null}
                        isLoading={!!pendingSelectionId && detailLoading}
                    />
                )}
            </div>

            {/* Fixed: primary commit action pinned at the bottom of the
                pane — mt-auto soaks up the slack the capped viewer no
                longer takes. */}
            <div className="mt-auto shrink-0 space-y-2 border-t bg-background px-3 pb-3 pt-3">
                {usedInOtherSlot && (
                    <p className="text-xs text-amber-700">
                        Already in use for <span className="font-medium">{usedInOtherSlot}</span> — picking it
                        here will move the binding.
                    </p>
                )}
                <Button
                    onClick={onUseForSlot}
                    disabled={!pendingSelectionId || alreadyPickedForActiveSlot}
                    className="w-full"
                >
                    {alreadyPickedForActiveSlot ? 'Already filling this slot' : `Use for ${activeSlotName}`}
                </Button>
            </div>
        </section>
    );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const handle = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(handle);
    }, [value, delayMs]);
    return debounced;
}

function stripEmpty(filters: FilterFields): FilterFields {
    const out: FilterFields = {};
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null) continue;
        if (typeof v === 'string' && v.trim() === '') continue;
        (out as Record<string, unknown>)[k] = v;
    }
    return out;
}

function filterByText(items: IInputDatasetSummaryResponse[], q: string): IInputDatasetSummaryResponse[] {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => {
        const haystack = [
            it.patientName,
            it.patientId,
            it.accessionNumber,
            it.studyDescription,
            it.seriesDescription,
            it.modality,
        ]
            .filter((s): s is string => typeof s === 'string' && s.length > 0)
            .join(' ')
            .toLowerCase();
        return haystack.includes(needle);
    });
}

/**
 * Inline one-line summary shown beneath the series description in the
 * result list. Combines instance count and slice thickness when either
 * was extracted at commit. Returns null when neither field is on the
 * row so the caller can skip the empty line.
 */
function formatSeriesInline(item: IInputDatasetSummaryResponse): string | null {
    const parts: string[] = [];
    if (item.instanceCount != null) parts.push(`${item.instanceCount.toLocaleString()} slices`);
    if (item.sliceThicknessMm != null) parts.push(`${item.sliceThicknessMm.toFixed(2)} mm`);
    return parts.length > 0 ? parts.join(' · ') : null;
}

function groupByStudy(items: IInputDatasetSummaryResponse[]): StudyGroup[] {
    const groups: StudyGroup[] = [];
    for (const item of items) {
        const tail = groups[groups.length - 1];
        if (tail && tail.patientId === item.patientId && tail.studyDate === item.studyDate) {
            tail.items.push(item);
            continue;
        }
        groups.push({
            patientId: item.patientId,
            patientName: item.patientName,
            studyDate: item.studyDate,
            studyDescription: item.studyDescription,
            items: [item],
        });
    }
    return groups;
}

function uploadingPhaseLabel(phase: 'initiating' | 'chunks' | 'committing'): string {
    switch (phase) {
        case 'initiating':
            return 'Starting upload…';
        case 'chunks':
            return 'Uploading reconstructed volume…';
        case 'committing':
            return 'Finalising on the server…';
    }
}

function errorReasonLabel(reason: EagerUploadFailureReason): string {
    switch (reason) {
        case 'session_expired':
            return 'Upload session expired. Start over to retry.';
        case 'session_not_found':
            return 'Upload session was cancelled or no longer exists.';
        case 'commit_conflict':
            return 'Another upload of the same volume finished first.';
        case 'network':
            return 'Network problem during upload. Check your connection and retry.';
        case 'unknown':
            return 'Upload failed.';
    }
}
